import { forwardRef, RefObject } from 'react';
import { useTranslations } from 'next-intl';
import {
  IPost,
  IProduct,
  IProductSectionName,
  RoutesEnum,
} from '@monofront/types';
import ArticleCard, {
  Title,
  Subtitle,
  Image,
} from '@monofront/design-system/components/ArticleCard';
import {
  ButtonSizes,
  Carousel,
  CarouselSlide,
} from '@monofront/design-system/components/Carousel';
import useLocalizedRoutes from '@hooks/i18n/useLocalizedRoutes';

interface IProductAdvicesProps {
  product: IProduct;
  name: IProductSectionName;
  tips?: IPost[];
  ref: RefObject<HTMLHeadingElement | null>;
}

const ProductAdvices = forwardRef<HTMLHeadingElement, IProductAdvicesProps>(
  ({ product, name, tips }, ref) => {
    const tProducts = useTranslations('Products');
    const { getUrl } = useLocalizedRoutes();
    const advice = product.meta.marketing?.advice;

    if (!advice && !(tips && tips.length > 0)) return null;

    return (
      <>
        <h2
          ref={ref}
          id="section-advices"
          className="section-title tw-lf-title-sm tw-mb-6 tw-mt-[4.5rem] desktop:tw-lf-title-md"
          data-menu={
            name.menuTitle ? name.menuTitle : tProducts('advicesAndTips')
          }
          tabIndex={-1}
        >
          {name.sectionTitle
            ? name.sectionTitle.replace('%PRODUCT_NAME%', product.title)
            : tProducts('advicesAndTips')}
        </h2>

        {advice && (
          <div
            className="tw-lf-rich-text"
            dangerouslySetInnerHTML={{
              __html: advice,
            }}
          />
        )}

        {tips && tips.length > 0 && (
          <Carousel
            slidesPerView={'auto'}
            hasGradient
            navigation={true}
            freeMode={true}
            buttonSize={ButtonSizes.lg}
            hasCustomNagivation
            UNSAFE_className="max-[800px]:tw-lf-layout-container-full max-[800px]:[&_.swiper]:tw-px-4 max-[800px]:tw-overflow-hidden tw-mt-6 [&_.swiper]:tw-overflow-hidden"
            spaceBetween={16}
            customNavigationClasses={`
            tw-z-10
            [&_.swiper-button-disabled]:tw-hidden
            [&_.swiper-button-next]:!tw-right-[-26px]
            min-[1300px]:[&_.swiper-button-prev]:!tw-left-[-26px]
          `}
            breakpoints={{
              800: {
                freeMode: false,
                spaceBetween: 8,
              },
            }}
          >
            {tips.map((tip, index) => (
              <CarouselSlide key={index}>
                <ArticleCard
                  url={`${getUrl(RoutesEnum.BLOG_URL)}/${tip.slug}`}
                  isTargetBlank
                >
                  <Image
                    src={tip.image}
                    width={210}
                    height={130}
                    objectFit="cover"
                    alt={tip.title}
                  />
                  <Title>{tip.title}</Title>
                  <Subtitle>{tip.category_title}</Subtitle>
                </ArticleCard>
              </CarouselSlide>
            ))}
          </Carousel>
        )}
      </>
    );
  },
);

export default ProductAdvices;
